import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ReviewStep } from "./ReviewStep";
import { useAudienceReadiness } from "@/components/audiences/AudienceReadinessNotice";
import type { StudyType } from "../types";
import { getEffectiveRespondentLimits } from "../utils/respondentLimits";
import type {
  AudienceSelectionRef,
  StudyDesign,
  SurveyJson,
} from "@digital-twins/shared-types";
import {
  countStimulusCuts,
  maxRespondentsForDesign,
} from "@digital-twins/shared-types";
import type { Population } from "@/types/routes/tests";
import type { PopulationFilter } from "@/types/routes/test-results";
import { buildAudienceDescriptors } from "@/features/studies/report/page/buildAudienceDescriptors";

interface ReviewStepWrapperProps {
  studyType: StudyType;
  goalId: string | null;
  editedSurveyJson: SurveyJson;
  selectedPopulation: Population | undefined;
  filteredCount: number | null;
  /** The reviewed revision's `draft.audience`: what launch reads (C09-3). */
  reviewedAudience: AudienceSelectionRef | null;
  studyName: string;
  setStudyName: (name: string) => void;
  onCommitStudyName: () => Promise<boolean>;
  numRespondents: number | null;
  setNumRespondents: (num: number | null) => void;
  design: StudyDesign;
  setDesign: (design: StudyDesign) => void;
  creditInfo: {
    available?: number;
    allowOverage?: boolean;
    overageRateCentsPerCredit?: number;
  } | null;
  creditsPerRespondent: number;
  contentLanguage: string;
  studyMode?: 'quick' | 'full';
  onStudyModeChange?: (mode: 'quick' | 'full') => void;
  isLaunching: boolean;
  trialStudyLimitReached?: boolean;
  delivery?: {
    deckEnabled: boolean;
    twinChatEnabled: boolean;
    onDeckEnabledChange: (enabled: boolean) => void;
    onTwinChatEnabledChange: (enabled: boolean) => void;
  };
  onBack: () => void;
  onLaunch: () => Promise<void>;
}

export function ReviewStepWrapper({
  studyType,
  goalId,
  editedSurveyJson,
  selectedPopulation,
  filteredCount,
  reviewedAudience,
  studyName,
  setStudyName,
  onCommitStudyName,
  numRespondents,
  setNumRespondents,
  design,
  setDesign,
  creditInfo,
  creditsPerRespondent,
  contentLanguage,
  studyMode,
  onStudyModeChange,
  isLaunching,
  trialStudyLimitReached = false,
  delivery,
  onBack,
  onLaunch,
}: ReviewStepWrapperProps) {
  const { t } = useTranslation("app");
  const audienceReadiness = useAudienceReadiness(selectedPopulation?.id);
  const cutCount = useMemo(
    () => countStimulusCuts(editedSurveyJson),
    [editedSurveyJson],
  );

  const respondentLimits = useMemo(
    () =>
      getEffectiveRespondentLimits(studyType, {
        filteredCount,
        personaCount: selectedPopulation?.personaCount,
        designMax: maxRespondentsForDesign(design),
      }),
    [studyType, filteredCount, selectedPopulation?.personaCount, design],
  );

  // Display only: the respondent limits above keep their own inputs.
  const narrowing = useMemo(() => {
    const parts = [
      ...(reviewedAudience?.selectedSegments ?? []).map(
        (segment) => segment.label,
      ),
      ...buildAudienceDescriptors({
        populationFilter: (reviewedAudience?.populationFilter ?? undefined) as
          | PopulationFilter
          | undefined,
        filterLabels: reviewedAudience?.populationFilterLabels,
      }),
    ].filter((part) => part.trim().length > 0);
    return parts.length > 0 ? parts.join(" · ") : undefined;
  }, [reviewedAudience]);

  return (
    <div key="step5" className="relative flex-1 flex flex-col min-h-0">
      <ReviewStep
        studyType={studyType}
        goalId={goalId}
        respondentLimits={respondentLimits}
        hasAudience={selectedPopulation !== undefined}
        audience={{
          name:
            reviewedAudience?.populationName ||
            selectedPopulation?.name ||
            t("creation.noAudienceSelected"),
          personaCount: filteredCount ?? selectedPopulation?.personaCount,
          narrowing,
          tier: selectedPopulation?.tier,
          readiness: audienceReadiness,
        }}
        surveyJson={editedSurveyJson}
        surveyQuestionCount={(editedSurveyJson?.pages || []).reduce(
          (sum: number, page: NonNullable<SurveyJson["pages"]>[number]) =>
            sum + (Array.isArray(page?.elements) ? page.elements.length : 0),
          0,
        )}
        studyName={studyName}
        setStudyName={setStudyName}
        onCommitStudyName={onCommitStudyName}
        numRespondents={numRespondents}
        setNumRespondents={setNumRespondents}
        design={{ value: design, cutCount, onChange: setDesign }}
        contentLanguage={contentLanguage}
        creditInfo={{
          available: creditInfo?.available ?? -1,
          allowOverage: creditInfo?.allowOverage,
          overageRateCentsPerCredit: creditInfo?.overageRateCentsPerCredit,
        }}
        experimentCost={creditsPerRespondent}
        trialStudyLimitReached={trialStudyLimitReached}
        delivery={delivery}
        studyMode={studyMode}
        onStudyModeChange={onStudyModeChange}
        onBack={onBack}
        onLaunch={onLaunch}
        isLaunching={isLaunching}
      />
    </div>
  );
}
